import { HStack } from '@sgme/ui';
import clsx from 'clsx';
import { FormattedMessage, FormattedNumber } from 'react-intl';

import { ExpiredBadge } from '@/App/tiles/ForwardAccumulator/parts/PriceSummary/Pricing/ExpiredBadge.tsx';
import SalesTiering from '@/App/tiles/ForwardAccumulator/parts/PriceSummary/Pricing/SalesTiering.tsx';
import { useForwardAccumulatorSelector } from '@/App/tiles/ForwardAccumulator/ProductProvider.tsx';
import {
  getForwardAccumulatorQuote,
  getForwardAccumulatorRfsPremiumData,
} from '@/store/forwardAccumulator/selectors.ts';

interface PricingDisplayProps {
  isInternalSales: boolean;
}

export function PricingDisplay({ isInternalSales }: PricingDisplayProps) {
  const currency1 = useForwardAccumulatorSelector((accumulator) => accumulator.properties.currencyPair?.split('/')[0]);
  const currency2 = useForwardAccumulatorSelector((accumulator) => accumulator.properties.currencyPair?.split('/')[1]);
  const premiumTypeCurrencyIndex = useForwardAccumulatorSelector(
    (accumulator) => accumulator.properties.premiumTypeCurrencyIndex ?? 1,
  );
  const status = useForwardAccumulatorSelector((accumulator) => accumulator.properties.rfs?.status ?? 'NOT_PRICEABLE');
  const shouldShowTiering = useForwardAccumulatorSelector(
    (accumulator) => getForwardAccumulatorQuote(accumulator)?.tiering === 'STREAMDEFAULT',
  );

  const isPremiumReceive = useForwardAccumulatorSelector((accumulator) => {
    const premiumData = getForwardAccumulatorRfsPremiumData(getForwardAccumulatorQuote(accumulator));
    if (premiumData === undefined) {
      return false;
    }
    const idx = (accumulator.properties.premiumTypeCurrencyIndex ?? 1) - 1;
    return (premiumData.premiumAmount[idx] ?? 0) < 0;
  });

  const displayedCurrency = premiumTypeCurrencyIndex === 1 ? currency1 : currency2;

  return (
    <div className="d-flex flex-column justify-content-between h-100" data-e2e="pricing-display">
      <HStack horizontalAlignment="start" gap="16px">
        <div className="text-nowrap fs-12 line-height-1 mx-1">
          <FormattedMessage
            id={`accumulator.rates.streaming.AskAmount.${isPremiumReceive ? 'receive' : 'pay'}`}
            values={{ isInternalSales }}
          />
        </div>
        {status === 'EXPIRED' ? <ExpiredBadge /> : <SalesTiering shouldShowTiering={shouldShowTiering} />}
      </HStack>
      <PremiumAmountValue displayedCurrency={displayedCurrency} isInternalSales={isInternalSales} />
      <PremiumPercentValue displayedCurrency={displayedCurrency} />
    </div>
  );
}

function PremiumAmountValue({
  displayedCurrency,
  isInternalSales,
}: {
  displayedCurrency: string | undefined;
  isInternalSales: boolean;
}) {
  const premiumAmount = useForwardAccumulatorSelector((accumulator) => {
    const premiumData = getForwardAccumulatorRfsPremiumData(getForwardAccumulatorQuote(accumulator));
    if (premiumData === undefined) {
      return 0;
    }
    const idx = (accumulator.properties.premiumTypeCurrencyIndex ?? 1) - 1;
    return premiumData.premiumAmount[idx] ?? 0;
  });

  return (
    <div className={clsx('align-self-end pe-48px', { 'pt-8px': isInternalSales })} data-e2e="major-text">
      <span className="fs-30">
        <FormattedNumber value={Math.abs(premiumAmount)} minimumFractionDigits={2} />
      </span>
      <span className="fs-21 ms-8px">{displayedCurrency}</span>
    </div>
  );
}

function PremiumPercentValue({ displayedCurrency }: { displayedCurrency: string | undefined }) {
  const premiumInPercent = useForwardAccumulatorSelector((accumulator) => {
    const premiumData = getForwardAccumulatorRfsPremiumData(getForwardAccumulatorQuote(accumulator));
    if (premiumData === undefined) {
      return 0;
    }
    const idx = (accumulator.properties.premiumTypeCurrencyIndex ?? 1) - 1;
    return premiumData.premiumPercent[idx] ?? 0;
  });

  return (
    <div className="text-nowrap fs-12 line-height-1 ps-1">
      <FormattedNumber value={Math.abs(premiumInPercent)} minimumFractionDigits={4} />
      &nbsp;%&nbsp;{displayedCurrency}
    </div>
  );
}
